/**
 * GlobalErrorBoundary — Top-level error boundary that wraps the entire app.
 *
 * Unlike the component-level ErrorBoundary (which wraps individual sections),
 * this catches ANY unhandled React render error and shows a full-page fallback
 * instead of a white screen.
 *
 * Features:
 *   - Logs crash details to console
 *   - Offers "reload page" and "reset" actions
 *   - Shows the error message in a developer-friendly format
 */

import React from "react";
import { AlertTriangle, RotateCcw, RefreshCw, Home } from "lucide-react";

const btnReset =
  "appearance-none border-none font-inherit cursor-pointer p-0 m-0 leading-normal";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class GlobalErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo });

    console.error("[GlobalErrorBoundary] App crashed:", error);
    console.error("[GlobalErrorBoundary] Component stack:", errorInfo.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.hash = "#/";
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const errorMsg = this.state.error?.message || "\u05E9\u05D2\u05D9\u05D0\u05D4 \u05DC\u05D0 \u05E6\u05E4\u05D5\u05D9\u05D4";

    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        dir="rtl"
        style={{
          background: "linear-gradient(135deg, #eef1f8 0%, #e2e8f0 100%)",
          fontFamily: "var(--font-family, 'Segoe UI', Tahoma, sans-serif)",
        }}
      >
        <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-8 text-center">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-5">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>

          {/* Title */}
          <h1
            className="text-xl text-gray-800 mb-2"
            style={{ fontWeight: 700 }}
          >
            {"\u05D0\u05D5\u05E4\u05E1! \u05DE\u05E9\u05D4\u05D5 \u05D4\u05E9\u05EA\u05D1\u05E9"}
          </h1>

          {/* Description */}
          <p className="text-sm text-gray-500 mb-4 leading-relaxed">
            {"\u05D0\u05D9\u05E8\u05E2\u05D4 \u05E9\u05D2\u05D9\u05D0\u05D4 \u05DC\u05D0 \u05E6\u05E4\u05D5\u05D9\u05D4 \u05D1\u05DE\u05E2\u05E8\u05DB\u05EA."}{" "}
            {"\u05D4\u05E0\u05EA\u05D5\u05E0\u05D9\u05DD \u05E9\u05DC\u05DA \u05E9\u05DE\u05D5\u05E8\u05D9\u05DD \u05D1\u05E9\u05E8\u05EA \u05D5\u05DC\u05D0 \u05E0\u05E4\u05D2\u05E2\u05D5."}{" "}
            {"\u05E0\u05E1\u05D9 \u05DC\u05E8\u05E2\u05E0\u05DF \u05D0\u05EA \u05D4\u05D3\u05E3 \u05D0\u05D5 \u05DC\u05D7\u05D6\u05D5\u05E8 \u05DC\u05DE\u05E1\u05DA \u05D4\u05E8\u05D0\u05E9\u05D9."}
          </p>

          {/* Error details (collapsible) */}
          <details className="text-right mb-6">
            <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600 mb-2">
              {"\u05E4\u05E8\u05D8\u05D9\u05DD \u05D8\u05DB\u05E0\u05D9\u05D9\u05DD"}
            </summary>
            <pre
              className="text-[11px] text-red-600 bg-red-50 border border-red-200 rounded-lg p-3 overflow-auto max-h-40 whitespace-pre-wrap"
              dir="ltr"
            >
              {errorMsg}
              {this.state.error?.stack && (
                <>
                  {"\n\n"}
                  {this.state.error.stack.slice(0, 800)}
                </>
              )}
            </pre>
          </details>

          {/* Action buttons */}
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={this.handleReload}
              className={`${btnReset} flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm text-white shadow-sm hover:opacity-90`}
              style={{ fontWeight: 600, backgroundColor: "#2563eb" }}
            >
              <RefreshCw className="w-4 h-4" />
              {"\u05E8\u05E2\u05E0\u05DF \u05D3\u05E3"}
            </button>
            <button
              type="button"
              onClick={this.handleGoHome}
              className={`${btnReset} flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm border border-gray-200 text-gray-700 hover:bg-gray-50`}
              style={{ fontWeight: 500 }}
            >
              <Home className="w-4 h-4" />
              {"\u05D3\u05E3 \u05E8\u05D0\u05E9\u05D9"}
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              className={`${btnReset} flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm border border-gray-200 text-gray-700 hover:bg-gray-50`}
              style={{ fontWeight: 500 }}
            >
              <RotateCcw className="w-4 h-4" />
              {"\u05E0\u05E1\u05D4 \u05E9\u05D5\u05D1"}
            </button>
          </div>

          {/* Branding */}
          <p className="text-[10px] text-gray-300 mt-6">
            ZAP WEB v1.0
          </p>
        </div>
      </div>
    );
  }
}